import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import { AnchorHTMLAttributes, ReactNode } from 'react';
import { NAVIGATION_FROM_APP_CODE } from '@/constants/common';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import DeHaatCenter from '@/images/crop-problem/dehaat-center.webp';
import { AIMS_APP_CODE } from '@/models/Auth';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
// import { trackCustomEvent } from '@/utils/analytics/googleAnalytics'
import { getCookieValue } from '@/utils/helper';
import DirectionsIcon from './icons/DirectionsIcon';
import PhoneIcon from './icons/PhoneIcon';
import Paragraph, { VARIANT as PARAGRAPH_VARIANT } from './Paragraph';
import SubHeading, { VARIANT as SUBHEADING_VARIANT } from './SubHeading';
export enum VARIANT {
  CART_PICKUP,
  GREEN,
}
interface Props {
  address: string;
  containerClassName?: string;
  distance?: number;
  latitude?: number;
  longitude?: number;
  name: string;
  phone_number?: string;
  variant?: VARIANT;
}
interface AnchorButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  className: string;
  icon: ReactNode;
  label: string;
  href: string;
}
const AnchorButton = ({
  className,
  icon,
  label,
  href,
  ...props
}: AnchorButtonProps) => {
  return <a href={href} className={`flex justify-center py-2.5 rounded-lg overflow-hidden ${className}`} {...props} data-sentry-component="AnchorButton" data-sentry-source-file="DCCard.tsx">
      {icon}
      <span className="pl-1 font-nato-semibold text-base leading-[22px]">
        {label}
      </span>
    </a>;
};
const DCCard = ({
  address,
  containerClassName = '',
  distance,
  latitude,
  longitude,
  name,
  phone_number,
  variant = VARIANT.GREEN
}: Props) => {
  const {
    t
  } = useTranslation('nearbyDc');
  const isActionBtnHided = getCookieValue(NAVIGATION_FROM_APP_CODE) === AIMS_APP_CODE;
  const {
    trackCustomEvent
  } = useTriggerEvents();
  return <section className={`overflow-hidden p-4 relative rounded-xl ${containerClassName} ${variant === VARIANT.GREEN ? 'bg-olivine-20 border-2 border-olivine-80' : 'bg-orange-tint-10 border border-orange-tint-20'}`} data-sentry-component="DCCard" data-sentry-source-file="DCCard.tsx">
      {variant === VARIANT.GREEN ? <>
          <div className="-top-14 -left-12 absolute bg-olivine-80 opacity-20 rounded-full w-36 h-36"></div>
          <div className="-right-8 -bottom-10 absolute bg-olivine-80 opacity-20 rounded-full w-20 h-20"></div>
        </> : null}
      <header className="z-10 relative flex">
        <section className="relative bg-white px-1 py-2 rounded-lg w-12 h-12 grow-0 shrink-0">
          <Image src={DeHaatCenter} height={32} width={40} alt="Representation for DeHaat center" aria-hidden role="presentation" data-sentry-element="Image" data-sentry-source-file="DCCard.tsx" />
        </section>
        <section className="ml-4 text-neutral-90">
          <SubHeading variant={SUBHEADING_VARIANT.SMALL} data-sentry-element="SubHeading" data-sentry-source-file="DCCard.tsx">{name}</SubHeading>
          <Paragraph variant={PARAGRAPH_VARIANT.MEDIUM} className="mt-1.5" data-sentry-element="Paragraph" data-sentry-source-file="DCCard.tsx">
            {address}
          </Paragraph>
          {distance ? <Paragraph variant={PARAGRAPH_VARIANT.MEDIUM} className="mt-2.5">
              {t('distance')} {distance}
              {t('km')}
            </Paragraph> : null}
        </section>
      </header>
      {phone_number || latitude != null && longitude != null ? <footer className="z-10 relative gap-x-3 grid grid-cols-2 mt-4">
          {!isActionBtnHided && latitude != null && longitude != null ? <AnchorButton icon={<DirectionsIcon className="fill-primary-100 w-[23px] h-6" />} label={t('direction')} href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`} className="bg-white text-primary-100" target="_blank" rel="nofollow noreferrer" onClick={() => trackCustomEvent(CustomEventTypes.VENDOR_DIRECTION, address)} /> : null}
          {!isActionBtnHided && phone_number ? <AnchorButton icon={<PhoneIcon className="fill-white w-[23px] h-6" />} label={t('call_shop')} href={`tel:${phone_number}`} className="bg-primary-100 text-white" onClick={() => trackCustomEvent(CustomEventTypes.VENDOR_CALL_SHOP, address)} /> : null}
        </footer> : null}
    </section>;
};
export default DCCard;